import { useState, useEffect, useRef, useCallback } from 'react';

const isAbortError = (err) => err?.name === 'AbortError';

/**
 * Custom hook for managing auto-update functionality
 * Provides polling capabilities with configurable intervals and conditions
 * @param {(signal: AbortSignal) => void | Promise<void>} updateFunction — called with an AbortSignal per run
 */
export const useAutoUpdate = ({
    updateFunction,
    interval = 60000, // 1 minute
    enabled = true,
    conditions = [],
    onError = null,
    onSuccess = null,
    onPollingStart = null,
    maxRetries = 3,
    retryDelay = 1000
}) => {
    const [isPolling, setIsPolling] = useState(false);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    const [lastUpdate, setLastUpdate] = useState(null);
    const pollingRef = useRef(null);
    const retryTimeoutRef = useRef(null);
    const activePollAbortRef = useRef(null);

    // Check if all conditions are met for polling
    const shouldPoll = useCallback(() => {
        if (!enabled || !updateFunction) return false;
        return conditions.every(condition => {
            if (typeof condition === 'function') {
                return condition();
            }
            return Boolean(condition);
        });
    }, [enabled, updateFunction, conditions]);

    const stopPolling = useCallback(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }

        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }

        activePollAbortRef.current?.abort();
        activePollAbortRef.current = null;

        setIsPolling(false);
    }, []);

    const startPolling = useCallback(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }

        activePollAbortRef.current?.abort();
        activePollAbortRef.current = null;

        if (!shouldPoll()) return;

        setError(null);
        setRetryCount(0);

        pollingRef.current = setInterval(async () => {
            if (!shouldPoll()) {
                stopPolling();
                return;
            }

            activePollAbortRef.current?.abort();
            const controller = new AbortController();
            activePollAbortRef.current = controller;
            const { signal } = controller;

            setIsPolling(true); // Set polling to true when executing
            if (onPollingStart) onPollingStart(); // Call the polling start callback
            try {
                await updateFunction(signal);
                setLastUpdate(new Date());
                setRetryCount(0);
                setError(null);
                if (onSuccess) onSuccess();
            } catch (err) {
                if (isAbortError(err)) {
                    if (activePollAbortRef.current === controller) {
                        activePollAbortRef.current = null;
                    }
                    setTimeout(() => setIsPolling(false), 0);
                    return;
                }
                console.error('Auto-update error:', err);
                setError(err);

                if (onError) onError(err);

                // Handle retries
                if (retryCount < maxRetries) {
                    setRetryCount(prev => prev + 1);

                    retryTimeoutRef.current = setTimeout(() => {
                        if (!shouldPoll()) return;
                        activePollAbortRef.current?.abort();
                        const retryController = new AbortController();
                        activePollAbortRef.current = retryController;
                        updateFunction(retryController.signal).catch((retryErr) => {
                            if (!isAbortError(retryErr)) {
                                console.error('Auto-update retry error:', retryErr);
                            }
                        });
                    }, retryDelay);
                } else {
                    stopPolling();
                }
            } finally {
                if (activePollAbortRef.current === controller) {
                    activePollAbortRef.current = null;
                }
                setTimeout(() => setIsPolling(false), 2000);
            }
        }, interval);
    }, [updateFunction, interval, shouldPoll, onSuccess, onError, onPollingStart, retryCount, maxRetries, retryDelay, stopPolling]);

    const forceUpdate = useCallback(async () => {
        if (!updateFunction) return;

        try {
            await updateFunction(undefined);
            setLastUpdate(new Date());
            setError(null);
            if (onSuccess) onSuccess();
        } catch (err) {
            if (isAbortError(err)) return;
            console.error('Force update error:', err);
            setError(err);
            if (onError) onError(err);
        }
    }, [updateFunction, onSuccess, onError]);

    // Auto-start/stop based on conditions
    useEffect(() => {
        if (shouldPoll()) {
            startPolling();
        } else {
            stopPolling();
        }

        return () => stopPolling();
    }, [shouldPoll, startPolling, stopPolling]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopPolling();
        };
    }, [stopPolling]);

    return {
        isPolling,
        error,
        lastUpdate,
        retryCount,
        startPolling,
        stopPolling,
        forceUpdate
    };
};

/**
 * Specialized hook for analysis auto-update
 * Automatically polls when analysis is in progress
 * @param {(signal?: AbortSignal) => void | Promise<void>} updateFunction
 */
export const useAnalysisAutoUpdate = ({
    analysisStatus,
    updateFunction,
    interval = 60000, // 1 minute
    enabled = true,
    onPollingStart = null
}) => {
    const shouldPollForAnalysis = useCallback(() => {
        return enabled &&
            analysisStatus &&
            (analysisStatus === 'IN_PROGRESS' || analysisStatus === 'PENDING');
    }, [enabled, analysisStatus]);

    return useAutoUpdate({
        updateFunction,
        interval,
        enabled: shouldPollForAnalysis(),
        conditions: [shouldPollForAnalysis],
        onError: (error) => {
            console.error('Analysis auto-update error:', error);
        },
        onSuccess: () => { },
        onPollingStart
    });
};

/**
 * Specialized hook for applicants list auto-update
 * Polls for general updates to the applicants list
 * @param {(signal: AbortSignal) => void | Promise<void>} updateFunction
 */
export const useApplicantsAutoUpdate = ({
    updateFunction,
    interval = 300000, // 5 minutes
    enabled = true,
    onPollingStart = null
}) => {
    const shouldPollForApplicants = useCallback(() => {
        return enabled && updateFunction;
    }, [enabled, updateFunction]);

    return useAutoUpdate({
        updateFunction,
        interval,
        enabled: shouldPollForApplicants(),
        conditions: [shouldPollForApplicants],
        onError: (error) => {
            console.error('Applicants auto-update error:', error);
        },
        onSuccess: () => { },
        onPollingStart
    });
};

